import { SupabaseClient } from '@supabase/supabase-js';
import { useQueryWithErrorBoundary, useSupabaseTypedClient } from 'hooks/reactQuery';

import { OtherTokenReward } from 'types/customEnums';
import { Plan_Type_Enum } from 'types/databaseEnums';
import { AiUsage, CurrentUser } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';
import { Database } from 'types/supabase';

const REFERRAL_CAP = 5;

const rechargeTokens: Record<Plan_Type_Enum, number> = {
  [Plan_Type_Enum.Free]: 3,
  [Plan_Type_Enum.Monthly]: 5,
  [Plan_Type_Enum.Yearly]: 10,
  [Plan_Type_Enum.Lifetime]: 15,
};

interface IGetCurrentUserQueryFnParams {
  supabase: SupabaseClient<Database>;
  userId: string;
}

export const getCurrentUserQueryFn = async ({
  supabase,
  userId,
}: IGetCurrentUserQueryFnParams): Promise<CurrentUser> => {
  const [
    { data, error: userError },
    userTokens,
    userExperience,
    { count, error },
    { count: codeFramesCount, error: codeFramesCountError },
    aiUsageResult,
    storageUsedResult,
    userPreferencesResult,
  ] = await Promise.all([
    supabase
      .from('user')
      .select(
        'id, name, screen_name, created_at, github_access_token, has_early_bird_badge, feature_flag_on, plan, public_email, private_email, avatar_url, github_url, discord_user_id, referral_code, is_terminated, has_full_access, onboarding_step, is_legacy, has_ban, storage_limit_bytes, location, bio, website_url'
      )
      .eq('id', userId)
      .single(),
    supabase
      .from('user_token')
      .select('tokens_available, extra_tokens, recycle_at')
      .eq('id', userId)
      .single(),
    supabase.rpc('get_total_experience', { p_user_id: userId }),
    supabase
      .from('user_action')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('token_reward_id', OtherTokenReward.Referral),
    supabase
      .from('user_code_frame')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId),
    supabase
      .from('user_ai_usage')
      .select('base_token_limit, base_tokens_used, extra_tokens_used, extra_token_limit')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('user_asset')
      .select('file_size_bytes')
      .eq('user_id', userId),
    supabase
      .from('user_preferences')
      .select('goal, experience_level, focus_area')
      .eq('user_id', userId)
      .maybeSingle(),
  ]);

  if (userError) throw userError;
  if (userTokens.error) throw userTokens.error;
  if (userExperience.error) throw userExperience.error;
  if (error) throw error;
  if (codeFramesCountError) throw codeFramesCountError;
  if (aiUsageResult.error) throw aiUsageResult.error;

  const user = data!;

  const tokensAvailable = userTokens.data?.tokens_available || 0;
  const extraTokens = userTokens.data?.extra_tokens || 0;

  const aiBaseTokensUsed = aiUsageResult.data?.base_tokens_used || 0;
  const aiExtraTokensUsed = aiUsageResult.data?.extra_tokens_used || 0;
  const aiTokensUsed = aiBaseTokensUsed + aiExtraTokensUsed;
  const aiBaseTokenLimit = aiUsageResult.data?.base_token_limit || 100000;
  const aiExtraTokenLimit = aiUsageResult.data?.extra_token_limit || 0;
  const aiTotalTokenLimit = aiBaseTokenLimit + aiExtraTokenLimit;
  const aiPercentUsed = aiTotalTokenLimit > 0 ? Math.round((aiTokensUsed / aiTotalTokenLimit) * 100) : 0;
  const aiBasePercentUsed = aiBaseTokenLimit > 0 ? Math.round((aiBaseTokensUsed / aiBaseTokenLimit) * 100) : 0;
  const aiExtraPercentUsed = aiExtraTokenLimit > 0 ? Math.round((aiExtraTokensUsed / aiExtraTokenLimit) * 100) : 0;
  const storageLimitBytes = user.storage_limit_bytes || 10485760;
  const storageUsedBytes = (storageUsedResult.data || []).reduce(
    (sum: number, row: { file_size_bytes: number }) => sum + (row.file_size_bytes || 0),
    0
  );

  const aiUsage: AiUsage = {
    aiTokensUsed,
    aiBaseTokensUsed,
    aiExtraTokensUsed,
    aiBaseTokenLimit,
    aiExtraTokenLimit,
    aiTotalTokenLimit,
    aiPercentUsed,
    aiBasePercentUsed,
    aiExtraPercentUsed,
    isAiLimitReached: aiBaseTokensUsed >= aiBaseTokenLimit && aiExtraTokensUsed >= aiExtraTokenLimit,
    storageLimitBytes,
    storageUsedBytes,
  };

  return {
    id: user.id,
    name: user.name,
    codeFramesCount: codeFramesCount || 0,
    screenName: user.screen_name || '',
    publicEmail: user.public_email || '',
    privateEmail: user.private_email,
    avatarUrl: user.avatar_url || '',
    githubUrl: user.github_url,
    location: user.location || '',
    bio: user.bio || '',
    websiteUrl: user.website_url || '',
    createdAt: user.created_at || '',
    isGithubConnected: Boolean(user.github_access_token),
    tokensAvailable,
    extraTokens,
    bigTokensCollected: tokensAvailable + extraTokens,
    rechargeTokens: rechargeTokens[user.plan as Plan_Type_Enum],
    experience: userExperience.data,
    isTerminated: Boolean(user.is_terminated),
    isFree: user.plan === Plan_Type_Enum.Free,
    isMonthlySubscriber: user.plan === Plan_Type_Enum.Monthly,
    isYearlySubscriber: user.plan === Plan_Type_Enum.Yearly,
    isLifetimeUser: user.plan === Plan_Type_Enum.Lifetime,
    isSuperUser: Boolean(user.has_full_access),
    hasDiscordRegistered: user.discord_user_id !== null,
    hasExhaustedReferralLink: (count || 0) >= REFERRAL_CAP,
    referralCode: user.referral_code || '',
    ...aiUsage,
    plan: user.plan as Plan_Type_Enum,
    nextTokenRecycleTime: new Date(userTokens.data?.recycle_at || ''),
    githubAccessToken: user.github_access_token || '',
    onboardingStep: user.onboarding_step || null,
    isLegacy: Boolean(user.is_legacy),
    hasBan: Boolean(user.has_ban),
    userPreferences: userPreferencesResult.data
      ? {
          goal: userPreferencesResult.data.goal,
          experience_level: userPreferencesResult.data.experience_level,
          focus_area: userPreferencesResult.data.focus_area,
        }
      : null,
  };
};

interface IUseGetCurrentUserResponse {
  data: CurrentUser | undefined | null;
  isPending: boolean;
}

interface IUseGetcurrentUserQueryParams {
  userId?: string;
}

export const useGetCurrentUserQuery = ({
  userId,
}: IUseGetcurrentUserQueryParams): IUseGetCurrentUserResponse => {
  const supabase = useSupabaseTypedClient();

  const { data, isPending } = useQueryWithErrorBoundary({
    queryKey: [TanstackQueryName.GetCurrentUser, userId],
    queryFn: () =>
      getCurrentUserQueryFn({
        supabase,
        userId: userId || '',
      }),
    enabled: Boolean(userId),
  });

  if (!data) {
    return {
      data: null,
      isPending,
    };
  }

  return {
    data,
    isPending,
  };
};
